/**
 * Car Data — Shared normalization, validation & constants
 *
 * Constants, validation, fuel normalization, engine extraction, and EEA helpers.
 * Make/model normalization is handled by the canonical vehicle DB (see canonical-resolver.js).
 *
 * ESM usage (frontend, webhook):
 *   import { CO2_MAX, normalizeFuelForDB } from '../utils/car-data.js';
 *
 * CJS usage (scripts):
 *   const { CO2_MAX, normalizeFuelForDB } = await import('../src/utils/car-data.js');
 */

// ============================================
// CONSTANTS
// ============================================

export const CO2_MIN = 0;
export const CO2_MAX = 400; // g/km — no production car exceeds this
export const ENGINE_CC_MIN = 500;
export const ENGINE_CC_MAX = 8500;

export const DISCODATA_URL = 'https://discodata.eea.europa.eu/sql';
export const CLAUDE_MODEL_ID = 'claude-haiku-4-5-20251001';

/** WLTP/NEDC conversion ratios from JRC research */
export const WLTP_NEDC_RATIOS = {
  petrol: 1.16,
  diesel: 1.26,
  default: 1.21,
};

// ============================================
// VALIDATION
// ============================================

export function isValidCO2(value) {
  if (value === null || value === undefined) return false;
  const n = Number(value);
  return !isNaN(n) && n >= CO2_MIN && n <= CO2_MAX;
}

export function isValidEngineCC(value) {
  if (value === null || value === undefined) return false;
  const n = Number(value);
  return !isNaN(n) && n >= ENGINE_CC_MIN && n <= ENGINE_CC_MAX;
}

export function validateCO2Result(result) {
  return {
    co2_wltp: isValidCO2(result.co2_wltp) ? Number(result.co2_wltp) : null,
    co2_nedc: isValidCO2(result.co2_nedc) ? Number(result.co2_nedc) : null,
    engine_cc: isValidEngineCC(result.engine_cc) ? Number(result.engine_cc) : null,
  };
}

// ============================================
// EEA-SPECIFIC MAKE / MODEL (UPPERCASE)
// ============================================

/**
 * Get UPPERCASE make for EEA Discodata queries.
 * With the canonical DB, make is already canonical (e.g. "Mercedes-Benz").
 * This just uppercases it for EEA matching.
 *
 * @param {string} make - Canonical make string (from canonical DB or raw)
 * @returns {string|null} UPPERCASE make for EEA query matching
 */
export function normalizeMakeForEEA(make) {
  if (!make) return null;
  const trimmed = make.trim();
  if (!trimmed) return null;
  return trimmed.toUpperCase();
}

/**
 * Get UPPERCASE model for EEA Discodata queries.
 * With the canonical DB, model is already specific (e.g. "C 220", "320").
 * Applies EEA-specific transformations for LIKE query matching.
 *
 * @param {string} model - Canonical model string (from canonical DB or raw)
 * @returns {string|null} UPPERCASE model for EEA query matching
 */
export function normalizeModelForEEA(model) {
  if (!model) return null;
  const trimmed = model.trim();
  if (!trimmed) return null;
  let m = trimmed.toUpperCase();

  // EEA-specific: strip "-CLASS" / " SERIES" suffixes for LIKE queries
  // "C-CLASS" → "C" (EEA uses "C 220", "C 300", etc.)
  m = m.replace(/^([A-Z])-CLASS$/, '$1');
  // "3 SERIES" → "3" (EEA uses "3", "320", etc.)
  m = m.replace(/^(\d)\s+SERIES$/, '$1');
  // "GRAN TURISMO" → "GT"
  m = m.replace(/\bGRAN TURISMO\b/, 'GT');

  return m;
}

// ============================================
// FUEL TYPE NORMALIZATION
// ============================================

/**
 * Normalize fuel type for database storage / UI display.
 * Returns human-readable values stored in car_listings.
 *
 * Handles English, Spanish, Portuguese, German variants.
 *
 * @param {string} fuelType - Raw fuel type
 * @returns {string|null} One of: diesel, petrol, hybrid, plug-in hybrid, electric, lpg, cng, or null
 */
export function normalizeFuelForDB(fuelType) {
  if (!fuelType) return null;
  const fuel = fuelType.toString().trim().toLowerCase();

  if (fuel.includes('diesel') && !fuel.includes('hybrid')) return 'diesel';
  if (fuel.includes('gasóleo') || fuel.includes('gasoleo')) return 'diesel';
  if (fuel.includes('petrol') || fuel.includes('gasoline') || fuel.includes('gasolina') || fuel.includes('benzin')) return 'petrol';
  if (fuel.includes('plug') && fuel.includes('hybrid')) return 'plug-in hybrid';
  if (fuel.includes('plugin_hybrid')) return 'plug-in hybrid';
  if (fuel.includes('hybrid') || fuel.includes('hibrido') || fuel.includes('híbrido')) return 'hybrid';
  if (fuel.includes('diesel') && fuel.includes('hybrid')) return 'hybrid';
  if (fuel.includes('electric') || fuel.includes('electrico') || fuel.includes('eléctrico') || fuel.includes('elétrico')) return 'electric';
  if (fuel.includes('lpg')) return 'lpg';
  if (fuel.includes('cng') || fuel.includes('gnc')) return 'cng';

  return null;
}

/**
 * Normalize fuel type for EEA Discodata queries and co2_reference matching.
 * Returns EEA-compatible values (different from DB storage format).
 *
 * Handles single-letter EEA CSV codes (P, Z, D, EL) and multilingual strings.
 *
 * @param {string} fuelType - Raw fuel type
 * @returns {string|null} One of: diesel, petrol, electric, petrol/electric, diesel/electric, lpg, ng, or null
 */
export function normalizeFuelForEEA(fuelType) {
  if (!fuelType) return null;
  const lower = fuelType.toString().trim().toLowerCase();

  // Single-letter EEA CSV codes
  if (lower === 'p') return 'petrol';
  if (lower === 'z' || lower === 'd') return 'diesel';
  if (lower === 'el') return 'electric';

  // Diesel/hybrid combo (check before plain diesel)
  if (lower.includes('diesel') && (lower.includes('hybrid') || lower.includes('electric'))) return 'diesel/electric';

  // Standard fuel types
  if (lower.includes('diesel') || lower.includes('gasóleo') || lower.includes('gasoleo')) return 'diesel';
  if (lower.includes('petrol') || lower.includes('gasoline') || lower.includes('gasolina') || lower.includes('benzin')) return 'petrol';
  if (lower.includes('electric') || lower.includes('eléctrico') || lower.includes('electrico') || lower.includes('elétrico')) return 'electric';

  // Hybrid types -> EEA "petrol/electric"
  if (lower.includes('plug') && lower.includes('hybrid')) return 'petrol/electric';
  if (lower.includes('plugin_hybrid')) return 'petrol/electric';
  if (lower.includes('híbrido plug-in')) return 'petrol/electric';
  if (lower.includes('hybrid') || lower.includes('híbrido') || lower.includes('hibrido')) return 'petrol/electric';

  // Alternative fuels
  if (lower.includes('lpg')) return 'lpg';
  if (lower.includes('cng') || lower.includes('ng')) return 'ng';

  return lower;
}

/**
 * Map fuel type to EEA Discodata Ft column SQL filter.
 * Handles all known EEA Ft value variants (case variations, abbreviations).
 *
 * IMPORTANT: Do NOT include 'petrol/electric' in petrol queries — PHEVs have
 * drastically lower CO2 (e.g. BMW 330e = 32 g/km vs 330i = 155 g/km)
 *
 * @param {string} fuelType - Raw fuel type
 * @returns {string} SQL filter fragment, e.g. "(Ft IN ('PETROL','petrol','Petrol','P'))"
 */
export function fuelTypeToEEAFilter(fuelType) {
  const normalized = normalizeFuelForEEA(fuelType);
  switch (normalized) {
    case 'diesel': return "(Ft IN ('DIESEL','diesel','Diesel','D'))";
    case 'petrol': return "(Ft IN ('PETROL','petrol','Petrol','P'))";
    case 'electric': return "(Ft IN ('ELECTRIC','electric','Electric','EL','BATTERY ELECTRIC'))";
    case 'petrol/electric': return "(Ft IN ('petrol/electric','PETROL/ELECTRIC','Petrol/Electric','PETROL-ELECTRIC','petrol / electric','PETROL / ELECTRIC'))";
    case 'diesel/electric': return "(Ft IN ('diesel/electric','DIESEL/ELECTRIC','Diesel/Electric','DIESEL-ELECTRIC'))";
    case 'lpg': return "(Ft IN ('lpg','LPG'))";
    case 'ng': return "(Ft IN ('ng','NG','CNG'))";
    default: return `(Ft='${normalized}')`;
  }
}

// ============================================
// ENGINE / TRANSMISSION EXTRACTION
// ============================================

/**
 * Extract engine capacity in cc from various string formats.
 * Handles: "1.5L", "2.0L", "1984 ccm", "2000 cm3", "1598 cc", "1984"
 *
 * @param {string} ccString - Raw engine capacity string
 * @returns {number|null} Engine capacity in cc (500-8500), or null
 */
export function extractEngineCC(ccString) {
  if (!ccString) return null;
  const str = ccString.toString().trim();

  // Handle "1.5L" or "2.0L" format -> multiply by 1000
  const literMatch = str.match(/^(\d+(?:\.\d+)?)\s*[lL]$/);
  if (literMatch) {
    const cc = Math.round(parseFloat(literMatch[1]) * 1000);
    return (cc >= ENGINE_CC_MIN && cc <= ENGINE_CC_MAX) ? cc : null;
  }

  // Handle standard formats: "1984 ccm", "2000 cm3", "1598 cc", "1984"
  const cleaned = str.replace(/\s/g, '').replace(/,/g, '').replace(/ccm|cm³|cc/gi, '');
  const cc = parseInt(cleaned);
  if (isNaN(cc)) return null;

  // Validate plausible range
  return (cc >= ENGINE_CC_MIN && cc <= ENGINE_CC_MAX) ? cc : null;
}

/**
 * Normalize transmission type.
 * Returns null for unknown values (not 'manual') to avoid biasing data.
 *
 * @param {string} transmission - Raw transmission string
 * @returns {string|null} One of: automatic, semi-automatic, manual, or null
 */
export function normalizeTransmission(transmission) {
  if (!transmission) return null;
  const trans = transmission.toLowerCase();
  if (trans.includes('semi')) return 'semi-automatic';
  if (trans.includes('auto') || trans.includes('automát')) return 'automatic';
  if (trans.includes('manual')) return 'manual';
  return null; // Unknown — don't default to 'manual'
}
